import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertOctagon, Gavel, Save, Siren } from 'lucide-react';
import { Button } from '@/components/Button';
import { Skeleton, SkeletonRegion } from '@/components/Skeleton';
import { cn } from '@/lib/cn';
import { formatarDataHora } from '@/lib/format';
import { assinatura, useRascunho } from '@/lib/rascunho';
import { chaves } from '@/features/tickets/tickets.api';
import { NomeDePessoa, useDicionarioDePessoas } from '@/features/tickets/pessoas';
import { AvisoDeFalha, AvisoDeRegistroMudado } from '@/features/workflow/componentes';
import { AprovacoesDoChamado } from '@/features/workflow/PainelDeAprovacoes';
import { classificarFalha, type Falha } from '@/features/workflow/recusa';
import { AgendamentoDaMudanca } from '@/features/changes/AgendamentoDaMudanca';
import { AvaliacaoDeRisco } from '@/features/changes/AvaliacaoDeRisco';
import { alterarMudanca, chavesDeMudanca, obterMudanca, registrarRevisao } from '@/features/changes/changes.api';
import {
  Campo,
  CLASSE_DE_CAMPO,
  Dado,
  EtiquetaDeDesfecho,
  EtiquetaDeRisco,
  EtiquetaDeTipoDeMudanca,
  Salvo,
  Secao,
} from '@/features/changes/componentes';
import { EscopoDaMudanca } from '@/features/changes/EscopoDaMudanca';
import {
  DESFECHOS,
  RESUMO_DE_TIPO_DE_MUDANCA,
  ROTULO_DE_DESFECHO,
  ROTULO_DE_TIPO_DE_MUDANCA,
  TIPOS_DE_MUDANCA,
  type Desfecho,
  type Mudanca,
  type TipoDeMudanca,
} from '@/features/changes/tipos';

/**
 * A ficha da mudança — a aba de Change Enablement dentro do chamado.
 *
 * ## Por que é uma aba do chamado, e não uma tela própria
 *
 * A mudança é extensão 1:1 do chamado (ADR-001): tem solicitante, comentários,
 * anexos, SLA e subchamados como qualquer outro. Dar a ela um endereço separado
 * criaria duas páginas para o mesmo registro, e a pessoa teria de escolher qual
 * das duas é "a mudança".
 *
 * ## O que esta tela deliberadamente NÃO faz
 *
 * Ela **não move a mudança**. Avançar é `POST /tickets/:id/transitions`, do
 * motor de workflow, e o controle disso já está no cabeçalho do chamado —
 * inclusive aprovar, inclusive concluir. O que se faz aqui é preencher o que o
 * guard depois confere: os planos, o risco, o escopo e a janela.
 */
export function PainelDeMudanca({ ticketId }: { ticketId: string }) {
  const consulta = useQuery({
    queryKey: chavesDeMudanca.mudanca(ticketId),
    queryFn: () => obterMudanca(ticketId),
  });

  if (consulta.isPending) {
    return (
      <SkeletonRegion label="Carregando a mudança" className="space-y-3">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-40 w-full" />
      </SkeletonRegion>
    );
  }
  if (consulta.isError) {
    return (
      <AvisoDeFalha
        falha={classificarFalha(consulta.error)}
        aoRecarregar={() => void consulta.refetch()}
      />
    );
  }

  const mudanca = consulta.data;

  return (
    /**
     * A chave remonta a aba inteira quando o chamado muda.
     *
     * Os formulários daqui guardam o que veio da mudança em estado local — é o
     * que permite editar cinco campos e salvar uma vez só. Mas o detalhe do
     * chamado **não** desmonta ao navegar de um chamado para outro: só os
     * parâmetros da rota mudam. Sem a chave, abrir a mudança seguinte pelo
     * calendário mostraria os planos da anterior, e salvar os gravaria no
     * chamado errado.
     */
    <div key={mudanca.ticketId} className="space-y-8">
      <Resumo mudanca={mudanca} />
      <TipoDaMudanca mudanca={mudanca} />
      <AvaliacaoDeRisco mudanca={mudanca} />
      <AgendamentoDaMudanca mudanca={mudanca} />
      <EscopoDaMudanca mudanca={mudanca} />
      <PlanosDaMudanca mudanca={mudanca} />
      <Secao
        titulo="Autorização"
        descricao="A aprovação não nasce de um botão desta tela: é um efeito da transição do fluxo, que escolhe a política pelo tipo e pelo risco. Aqui se acompanha e se decide."
      >
        <AprovacoesDoChamado ticketId={ticketId} />
      </Secao>
      <RevisaoPosImplementacao mudanca={mudanca} />
    </div>
  );
}

// ── resumo ─────────────────────────────────────────────────────────────────

function Resumo({ mudanca }: { mudanca: Mudanca }) {
  const pessoas = useDicionarioDePessoas([mudanca.pirById]);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <EtiquetaDeTipoDeMudanca tipo={mudanca.changeType} />
        <EtiquetaDeRisco nivel={mudanca.riskLevel} escore={mudanca.riskScore} />
        {mudanca.outcome ? <EtiquetaDeDesfecho desfecho={mudanca.outcome} /> : null}
        {mudanca.detectedRetroactively ? (
          <span
            className="inline-flex items-center gap-1 rounded-full bg-destructive/12 px-2 py-0.5 text-xs font-semibold text-destructive ring-1 ring-inset ring-destructive/25"
            title="Registrada depois do fato, para explicar uma alteração detectada no ambiente. É indicador de auditoria, e não se desmarca por esta tela."
          >
            <AlertOctagon className="h-3 w-3" aria-hidden />
            registrada depois do fato
          </span>
        ) : null}
      </div>

      {mudanca.changeType === 'EMERGENCY' ? <FaixaDeEmergencia /> : null}

      <dl className="grid gap-x-6 gap-y-3 rounded-lg border border-border bg-card px-5 py-4 text-sm shadow-card sm:grid-cols-3">
        <Dado
          termo="Início planejado"
          valor={mudanca.plannedStart ? formatarDataHora(mudanca.plannedStart) : '—'}
        />
        <Dado
          termo="Fim planejado"
          valor={mudanca.plannedEnd ? formatarDataHora(mudanca.plannedEnd) : '—'}
        />
        <Dado
          termo="Revisão pós-implementação"
          valor={
            mudanca.pirCompletedAt ? (
              <>
                {formatarDataHora(mudanca.pirCompletedAt)} ·{' '}
                <NomeDePessoa id={mudanca.pirById} pessoas={pessoas} />
              </>
            ) : (
              'ainda não registrada'
            )
          }
        />
      </dl>

      {mudanca.rollbackReason ? (
        <p className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          <strong>Motivo da reversão:</strong> {mudanca.rollbackReason}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Mudança emergencial aprova **depois** — critério 4.11.
 *
 * A regra vive na definição do fluxo `change-emergency`, e uma regra que só
 * existe no JSON de um workflow é uma regra que ninguém que usa a tela conhece.
 * Aqui ela é a primeira coisa que se lê na ficha, com a ordem desenhada: o
 * comitê emergencial entra depois da validação, não antes da implementação.
 */
function FaixaDeEmergencia() {
  const passos = [
    { nome: 'Rascunho', destaque: false },
    { nome: 'Implementação', destaque: false },
    { nome: 'Validação', destaque: false },
    { nome: 'Comitê Emergencial (ECAB)', destaque: true },
    { nome: 'Concluída', destaque: false },
  ];
  return (
    <div className="space-y-2 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3">
      <p className="flex items-center gap-2 text-sm font-semibold text-destructive">
        <Siren className="h-4 w-4" aria-hidden />
        Esta mudança é autorizada agora e ratificada depois
      </p>
      <p className="text-sm text-foreground/90">
        Numa mudança emergencial a aprovação vem <strong>no fim</strong>: quem autoriza a execução é
        quem tem a permissão de emergência, e o Comitê Emergencial revisa o que já aconteceu — com
        quórum reduzido e prazo de quatro horas. No vencimento o pedido escala, e não é reprovado
        sozinho: rejeitar por decurso de prazo mandaria desfazer uma mudança já implementada.
      </p>
      <ol className="flex flex-wrap items-center gap-1 pt-1 text-xs">
        {passos.map((passo, indice) => (
          <li key={passo.nome} className="flex items-center gap-1">
            <span
              className={cn(
                'rounded-full px-2 py-0.5',
                passo.destaque
                  ? 'bg-destructive font-semibold text-destructive-foreground'
                  : 'bg-muted text-muted-foreground',
              )}
            >
              {passo.destaque ? <Gavel className="mr-1 inline h-3 w-3" aria-hidden /> : null}
              {passo.nome}
            </span>
            {indice < passos.length - 1 ? (
              <span aria-hidden className="text-muted-foreground">
                →
              </span>
            ) : null}
          </li>
        ))}
      </ol>
    </div>
  );
}

// ── tipo ───────────────────────────────────────────────────────────────────

/**
 * O tipo escolhe o fluxo, e o fluxo escolhe quem aprova.
 *
 * Depois de o chamado estar vinculado a uma versão de fluxo, trocar o tipo é
 * migração de workflow — e o servidor responde 409. A tela mostra a razão em
 * vez de deixar a pessoa descobrir pelo erro.
 */
function TipoDaMudanca({ mudanca }: { mudanca: Mudanca }) {
  const cliente = useQueryClient();
  const [falha, setFalha] = useState<Falha | null>(null);
  const travado = mudanca.ticket.workflowVersionId !== null;

  const trocar = useMutation({
    mutationFn: (changeType: TipoDeMudanca) =>
      alterarMudanca(mudanca.ticketId, { changeType, version: mudanca.version }),
    onMutate: () => setFalha(null),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: chaves.raiz }),
    onError: (erro) => setFalha(classificarFalha(erro)),
  });

  return (
    <Secao
      titulo="Tipo da mudança"
      descricao="Padrão, normal e emergencial são três fluxos publicados, e não três valores de um combo: o vínculo do chamado escolhe o fluxo, e nenhum código pergunta “é padrão?”."
    >
      <div className="grid gap-2 sm:grid-cols-3">
        {TIPOS_DE_MUDANCA.map((tipo) => {
          const atual = mudanca.changeType === tipo;
          return (
            <button
              key={tipo}
              type="button"
              disabled={travado || trocar.isPending}
              onClick={() => trocar.mutate(tipo)}
              aria-pressed={atual}
              className={cn(
                'space-y-1 rounded-md border px-3 py-2 text-left text-sm transition-colors',
                atual ? 'border-primary bg-primary/10' : 'border-border hover:bg-accent',
                (travado || trocar.isPending) && 'cursor-not-allowed opacity-70 hover:bg-transparent',
              )}
            >
              <span className="block font-semibold">{ROTULO_DE_TIPO_DE_MUDANCA[tipo]}</span>
              <span className="block text-xs text-muted-foreground">
                {RESUMO_DE_TIPO_DE_MUDANCA[tipo]}
              </span>
            </button>
          );
        })}
      </div>

      {travado ? (
        <p className="text-xs text-muted-foreground">
          O chamado já segue um fluxo, e o fluxo é escolhido pelo tipo. Trocar agora seria migração
          de workflow — operação própria, para que uma mudança emergencial não ande pelo grafo da
          normal sem que nenhuma tela mostre a discrepância.
        </p>
      ) : null}

      {falha ? (
        <AvisoDeFalha
          falha={falha}
          aoRecarregar={() =>
            void cliente.refetchQueries({ queryKey: chaves.item(mudanca.ticketId) })
          }
        />
      ) : null}
    </Secao>
  );
}

// ── planos ─────────────────────────────────────────────────────────────────

const PLANOS = [
  {
    campo: 'justification' as const,
    rotulo: 'Justificativa',
    dica: 'Por que a mudança precisa acontecer. Exigida pelo fluxo emergencial antes de autorizar.',
    linhas: 3,
  },
  {
    campo: 'implementationPlan' as const,
    rotulo: 'Plano de implementação',
    dica: 'O passo a passo do que será feito. Exigido para agendar nos três fluxos.',
    linhas: 6,
  },
  {
    campo: 'testPlan' as const,
    rotulo: 'Plano de teste',
    dica: 'Como se verifica que deu certo — Service Validation and Testing do ITIL.',
    linhas: 4,
  },
  {
    campo: 'rollbackPlan' as const,
    rotulo: 'Plano de retorno',
    dica: 'Inegociável nos três fluxos: mudança que não se desfaz não se agenda.',
    linhas: 5,
  },
  {
    campo: 'implementationEvidence' as const,
    rotulo: 'Evidência da implementação',
    dica: 'O que ficou registrado da execução. Exigida para enviar à validação.',
    linhas: 4,
  },
];

function PlanosDaMudanca({ mudanca }: { mudanca: Mudanca }) {
  const cliente = useQueryClient();
  const [falha, setFalha] = useState<Falha | null>(null);

  /**
   * O texto, marcado pela assinatura dos **cinco campos deste formulário**.
   *
   * Semear com `useState(() => …)` era a sobrescrita silenciosa: basta um
   * painel irmão desta mesma aba invalidar a raiz de chamados para a consulta
   * refazer, e aí os campos ficam com o texto antigo. Salvar por cima apagaria
   * o que outra pessoa gravou no intervalo.
   *
   * A marca é o conteúdo e não `mudanca.version` de propósito: avaliar o risco
   * ou agendar — dois painéis acima, na mesma tela — incrementa a versão sem
   * tocar em plano nenhum, e travar a gravação por causa disso seria punir a
   * pessoa por uma ação dela mesma. Ver `@/lib/rascunho`.
   */
  const rascunho = useRascunho(
    () => ({
      justification: mudanca.justification ?? '',
      implementationPlan: mudanca.implementationPlan ?? '',
      testPlan: mudanca.testPlan ?? '',
      rollbackPlan: mudanca.rollbackPlan ?? '',
      implementationEvidence: mudanca.implementationEvidence ?? '',
    }),
    assinatura(
      mudanca.justification,
      mudanca.implementationPlan,
      mudanca.testPlan,
      mudanca.rollbackPlan,
      mudanca.implementationEvidence,
    ),
  );

  const salvar = useMutation({
    mutationFn: () =>
      alterarMudanca(mudanca.ticketId, {
        justification: rascunho.valor.justification,
        implementationPlan: rascunho.valor.implementationPlan,
        testPlan: rascunho.valor.testPlan,
        rollbackPlan: rascunho.valor.rollbackPlan,
        implementationEvidence: rascunho.valor.implementationEvidence,
        version: mudanca.version,
      }),
    onMutate: () => setFalha(null),
    onSuccess: () => {
      // O texto na tela passa a ser o que vale; a ressemeadura vem sozinha
      // quando a consulta refizer, com o mesmo conteúdo.
      rascunho.confirmar();
      void cliente.invalidateQueries({ queryKey: chaves.raiz });
    },
    onError: (erro) => setFalha(classificarFalha(erro)),
  });

  return (
    <Secao
      titulo="Planos"
      descricao="O plano de implementação e o de retorno são o que a aprovação examinou. Alterá-los depois de aprovado deixa a autorização apoiada num texto que já não existe — confira o painel de Autorização antes de seguir. A invalidação automática da aprovação (critério 3.7) ainda não é feita pelo servidor: hoje a decisão é de quem lê."
      acao={
        <div className="flex items-center gap-2">
          <Salvo visivel={salvar.isSuccess} />
          <Button
            size="sm"
            variant="secondary"
            loading={salvar.isPending}
            disabled={rascunho.desatualizado}
            onClick={() => salvar.mutate()}
            icon={<Save className="h-4 w-4" aria-hidden />}
          >
            Salvar planos
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {PLANOS.map((plano) => (
          <Campo
            key={plano.campo}
            rotulo={plano.rotulo}
            htmlFor={`plano-${plano.campo}`}
            dica={plano.dica}
          >
            <textarea
              id={`plano-${plano.campo}`}
              rows={plano.linhas}
              value={rascunho.valor[plano.campo]}
              onChange={(evento) => {
                // Mexer no plano apaga o "salvo": o aviso verde ao lado de um
                // texto ainda não gravado é pior do que nenhum aviso.
                salvar.reset();
                const texto = evento.target.value;
                rascunho.definir((atual) => ({ ...atual, [plano.campo]: texto }));
              }}
              className={CLASSE_DE_CAMPO}
            />
          </Campo>
        ))}
      </div>

      {rascunho.desatualizado ? <AvisoDeRegistroMudado aoDescartar={rascunho.redefinir} /> : null}

      {falha ? (
        <AvisoDeFalha
          falha={falha}
          aoRecarregar={() => {
            rascunho.redefinir();
            void cliente.refetchQueries({ queryKey: chaves.item(mudanca.ticketId) });
          }}
        />
      ) : null}
    </Secao>
  );
}

// ── revisão pós-implementação ──────────────────────────────────────────────

const MINIMO_DA_NOTA = 10;

function RevisaoPosImplementacao({ mudanca }: { mudanca: Mudanca }) {
  const cliente = useQueryClient();
  const [falha, setFalha] = useState<Falha | null>(null);

  // Os três campos num rascunho só, marcado pelo conteúdo deles — mesma razão
  // de `PlanosDaMudanca`.
  const rascunho = useRascunho(
    () => ({
      desfecho: (mudanca.outcome ?? 'SUCCESSFUL') as Desfecho,
      nota: mudanca.pirNote ?? '',
      motivo: mudanca.rollbackReason ?? '',
    }),
    assinatura(mudanca.outcome, mudanca.pirNote, mudanca.rollbackReason),
  );
  const { desfecho, nota, motivo } = rascunho.valor;

  const registrar = useMutation({
    mutationFn: () =>
      registrarRevisao(mudanca.ticketId, {
        outcome: desfecho,
        pirNote: nota.trim(),
        ...(desfecho === 'ROLLED_BACK' ? { rollbackReason: motivo.trim() } : {}),
        version: mudanca.version,
      }),
    onMutate: () => setFalha(null),
    onSuccess: () => {
      rascunho.confirmar();
      void cliente.invalidateQueries({ queryKey: chaves.raiz });
    },
    onError: (erro) => setFalha(classificarFalha(erro)),
  });

  const incompleto =
    rascunho.desatualizado ||
    nota.trim().length < MINIMO_DA_NOTA ||
    (desfecho === 'ROLLED_BACK' && motivo.trim().length === 0);

  return (
    <Secao
      titulo="Revisão pós-implementação (PIR)"
      descricao="Revisão que não diz se deu certo não é revisão. O desfecho registrado aqui alimenta o indicador de mudanças com retorno, e é uma das origens do registro de melhoria contínua."
    >
      <fieldset className="space-y-1.5">
        <legend className="text-sm font-medium">Desfecho</legend>
        <div className="grid gap-1 sm:grid-cols-2">
          {DESFECHOS.map((valor) => (
            <label
              key={valor}
              className={cn(
                'flex cursor-pointer items-center gap-2 rounded-md border px-2.5 py-1.5 text-sm transition-colors',
                desfecho === valor ? 'border-primary bg-primary/10' : 'border-border hover:bg-accent',
              )}
            >
              <input
                type="radio"
                name="desfecho"
                value={valor}
                checked={desfecho === valor}
                onChange={() => {
                  registrar.reset();
                  rascunho.definir((atual) => ({ ...atual, desfecho: valor }));
                }}
              />
              {ROTULO_DE_DESFECHO[valor]}
            </label>
          ))}
        </div>
      </fieldset>

      <Campo
        rotulo="Nota da revisão"
        htmlFor="pir-nota"
        dica={`Pelo menos ${MINIMO_DA_NOTA} caracteres.`}
      >
        <textarea
          id="pir-nota"
          rows={4}
          value={nota}
          onChange={(evento) => {
            registrar.reset();
            const texto = evento.target.value;
            rascunho.definir((atual) => ({ ...atual, nota: texto }));
          }}
          className={CLASSE_DE_CAMPO}
        />
      </Campo>

      {desfecho === 'ROLLED_BACK' ? (
        <Campo
          rotulo="Motivo da reversão"
          htmlFor="pir-motivo"
          dica="Obrigatório: é o dado do indicador de mudanças com retorno."
        >
          <textarea
            id="pir-motivo"
            rows={2}
            value={motivo}
            onChange={(evento) => {
              registrar.reset();
              const texto = evento.target.value;
              rascunho.definir((atual) => ({ ...atual, motivo: texto }));
            }}
            className={CLASSE_DE_CAMPO}
          />
        </Campo>
      ) : null}

      {rascunho.desatualizado ? <AvisoDeRegistroMudado aoDescartar={rascunho.redefinir} /> : null}

      {falha ? (
        <AvisoDeFalha
          falha={falha}
          aoRecarregar={() => {
            rascunho.redefinir();
            void cliente.refetchQueries({ queryKey: chaves.item(mudanca.ticketId) });
          }}
        />
      ) : null}

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          loading={registrar.isPending}
          disabled={incompleto}
          onClick={() => registrar.mutate()}
        >
          Registrar revisão
        </Button>
        <Salvo visivel={registrar.isSuccess} texto="revisão registrada" />
      </div>
    </Secao>
  );
}
